import { Injectable, inject } from '@angular/core';
import { VoiceProduct } from '@shared/models/voice-product.model';
import {
  CaptureRequest,
  CaptureType,
  CreateSubscriptionWithGeneratedANumber,
  CreateSubscriptionWithPorting,
  CaptureSubscriber,
  AdditionalService,
  CreateMBBSubscription,
  CreateMBBSubscriptionWithHardware,
  CreateMBBSubscriptionWithHardwareAndLogistics,
  Equipment,
  ChangeSubscriptionOnExistingCustomer,
  AddEquipment,
  ChangeMbbSubscriptionOnExistingCustomer,
  UpdateServicesOnExistingSubscription,
  ChangeSubscriberOnExistingSubscription,
  CreateCellPhoneBundleWithCustomerSelectedANumber,
  CreateSubscriptionWithCustomerSelectedAnumber,
  CellPhoneAgreement,
  CreateCellPhoneBundleWithGeneratedANumber,
  SubscriptionWithCustomerSelectedANumber,
  SubscriptionWithGeneratedANumber,
  SubscriptionWithPorting,
  CreateCellPhoneBundleWithPorting,
  CellPhoneAgreementPrice,
  CreateCellPhoneBundleWithExistingSubscription,
  CaptureChangeSimCard,
  CaptureSendReturnBox,
  CaptureCreateCustomerService,
  AddMobileInsurance,
  SubscriptionInfo,
  CaptureTradeInProduct,
  CaptureVendorSubsidyVoucher,
  CreateAccessoryBundleWithCustomerSelectedANumber,
  CreateAccessoryBundleWithPorting,
  CreateAccessoryBundleWithExistingSubscription,
} from '../models/order-capture.model';
import { Store } from '@ngxs/store';
import {
  ShipmentOption,
  CustomerDetails,
  CustomerAddress,
  CustomerAllowance,
  CustomerDeliveryAddress,
  OrderStateModel,
  ProductOrderRequest,
  OrderCategories,
  RelatedParty,
  BypassInfoRequest,
  BypassCategory,
} from '@shared/models/order.model';
import { Dealer } from '@core/models/dealer.model';
import { LogisticsStateModel } from '../states/logistics/logistics.state';
import { MbbProduct } from '@sales/modules/mbb/models/mbb-product.model';
import { CustomerStateModel } from '@sales/modules/details/models/customer.model';
import { HardwareProduct } from '@shared/models/hardware-product.model';
import { SubscriptionHelperService } from './subscription-helper.service';
import { PaymentType, UpfrontPaymentMethod } from '@shared/enums/payment-type';
import { MobilePhoneProduct } from '../models/mobilephone-product';
import { MobilePhoneProductHelperService } from '../services/mobile-phone-product-helper.service';
import { Price } from '@shared/models/price.model';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { DeliveryOptionDetails } from '../models/delivery-options.model';
import { MbbDeliveryOptions } from '../modules/mbb/models/mbb-delivery-options.model';
import { MobilePaymentType } from '../enums/mobile-payment-type';
import { CustomerProductSelectors } from '../states/customer-product/customer-product.selectors';
import { FormSelectors } from '../states/form/form.selectors';
import { MobilBytteSelectors } from '../modules/mobilephone/states/mobilbytte/mobilbytte.selectors';
import { CustomerEquipmentUpgradeAgreement } from '../modules/mobilephone/models/equipment-upgrade-agreement.model';
import { CustomerProductMobilePhoneSelectors } from '../states/customer-product-mobilephone/customer-product-mobilephone.selectors';
import { HardwareVariant } from '../modules/mobilephone/models/mobilephone-variant';
import { ProductHelperService } from './product-helper.service';
import { TradeInSelectors } from '../states/customer-product-trade-in/customer-product-trade-in.selectors';
import { AmountPrice } from '../models/price.model';
import { AccessoryProductHelperService } from './accessory-product-helper.service';
import { UserSelectors } from '@app/core/states/user/user.selectors';
import { OrderSelectors } from '../states/order/order.selectors';
import { CustomerServiceProduct } from '@app/shared/models/customer-service-product';
import { getCaptureTypesForProduct } from '@app/sales/capture-type-utils';
import { hasAnnualBinding } from '@app/sales/bank-id-utils';
import { BindingType } from '@app/sales/models/binding-type.enums';
import { dateFns } from '@core/date-utils';

@Injectable({
  providedIn: 'root',
})
export class OrderCaptureHelperService {
  private store = inject(Store);

  getCaptureRequests(product: VoiceProduct | MbbProduct | HardwareProduct | MobilePhoneProduct): CaptureRequest[] {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const captureTypes: CaptureType[] = getCaptureTypesForProduct(product, allVoiceProducts);
    const captureRequests: CaptureRequest[] = [];
    captureTypes.forEach(captureType => {
      const payload = this.getPayload(captureType, product);
      const request = new CaptureRequest();
      request.captureType = captureType;
      request.payload = payload;
      captureRequests.push(request);
    });

    return captureRequests;
  }

  getProductName(product: VoiceProduct | MbbProduct | HardwareProduct | MobilePhoneProduct): string {
    if (ProductHelperService.isAccessoryProduct(product)) {
      return product.equipment?.name;
    } else if (ProductHelperService.isMbbProduct(product)) {
      return product.mbbSubscription.shortName;
    } else if (ProductHelperService.isMobileProduct(product)) {
      return product.mobilePhoneVariant?.name;
    } else {
      return product.offering.shortName;
    }
  }

  getProductDescription(product: VoiceProduct | MbbProduct | HardwareProduct | MobilePhoneProduct): string {
    if (ProductHelperService.isAccessoryProduct(product)) {
      return product.equipment?.description;
    } else if (ProductHelperService.isMbbProduct(product)) {
      return product.mbbSubscription.name;
    } else if (ProductHelperService.isMobileProduct(product)) {
      return product.mobilePhoneVariant?.name;
    } else {
      return product.offering.name;
    }
  }

  getReturnBoxRequest(): CaptureRequest {
    const payload = this.captureSendReturnBox();
    const request: CaptureRequest = {
      captureType: CaptureType.SendReturnBox,
      payload: payload,
    } as CaptureRequest;

    return request;
  }

  getCustomerServiceProductRequest(product: CustomerServiceProduct): CaptureRequest {
    const payload = this.captureCustomerServiceProduct(product);
    const request: CaptureRequest = {
      captureType: CaptureType.CreateCustomerService,
      payload: payload,
    } as CaptureRequest;

    return request;
  }

  getTradeInProductRequest(): CaptureRequest {
    const payload = this.captureTradeInProduct();
    const request: CaptureRequest = {
      captureType: CaptureType.AddTradeInVoucher,
      payload: payload,
    } as CaptureRequest;

    return request;
  }

  getVendorSubsidyVoucherRequest(): CaptureRequest {
    const payload = this.captureVendorSubsidyVoucher();
    const request: CaptureRequest = {
      captureType: CaptureType.AddVendorSubsidyVoucher,
      payload: payload,
    } as CaptureRequest;

    return request;
  }

  getPayload(captureType: CaptureType, product: VoiceProduct | MbbProduct | HardwareProduct | MobilePhoneProduct): string {
    let payload;
    const verification = this.store.selectSnapshot(FormSelectors.getCustomerVerification);

    switch (captureType) {
      case CaptureType.CreateSubscriptionWithPorting: {
        payload = this.createSubscriptionWithPorting(product as VoiceProduct);
        break;
      }
      case CaptureType.CreateSubscriptionWithGeneratedANumber: {
        payload = this.createSubscriptionWithGeneratedANumber(product as VoiceProduct);
        break;
      }
      case CaptureType.CreateSubscriptionWithCustomerSelectedANumber: {
        payload = this.createSubscriptionWithCustomerSelectedAnumber(product as VoiceProduct);
        break;
      }
      case CaptureType.CreateMBBSubscription: {
        payload = this.createMBBSubscription(product as MbbProduct);
        break;
      }
      case CaptureType.CreateMBBSubscriptionWithHardware: {
        payload = this.createMBBSubscriptionWithHardware(product as MbbProduct);
        break;
      }
      case CaptureType.CreateMBBSubscriptionWithHardwareAndLogistics: {
        payload = this.createMBBSubscriptionWithHardwareAndLogistics(product as MbbProduct);
        break;
      }
      case CaptureType.ChangeSubscriptionOnExistingCustomerWithSubscriber:
      case CaptureType.ChangeSubscriptionOnExistingCustomer: {
        payload = this.changeSubscriptionOnExistingCustomer(product as VoiceProduct);
        break;
      }
      case CaptureType.AddEquipment: {
        if (ProductHelperService.isAccessoryProduct(product)) {
          payload = this.addEquipment(product as HardwareProduct);
        } else if (ProductHelperService.isMobileProduct(product)) {
          payload = this.addMobileEquipment(product as MobilePhoneProduct);
        }
        break;
      }
      case CaptureType.ChangeMbbSubscriptionOnExistingCustomer: {
        payload = this.changeMbbSubscriptionOnExistingCustomer(product as MbbProduct);
        break;
      }
      case CaptureType.UpdateServicesOnExistingSubscription: {
        payload = this.updateServicesOnExistingSubscription(product as VoiceProduct);
        break;
      }
      case CaptureType.ChangeSubscriberOnExistingSubscription: {
        payload = this.changeSubscriberOnExistingSubscription(product as VoiceProduct);
        break;
      }
      case CaptureType.CreateCellPhoneBundleWithCustomerSelectedANumber: {
        payload = this.createCellPhoneBundleWithCustomerSelectedANumber(product as MobilePhoneProduct);
        break;
      }
      case CaptureType.CreateCellPhoneBundleWithGeneratedANumber: {
        payload = this.createCellPhoneBundleWithGeneratedANumber(product as MobilePhoneProduct);
        break;
      }
      case CaptureType.CreateCellPhoneBundleWithPorting: {
        payload = this.createCellPhoneBundleWithPorting(product as MobilePhoneProduct);
        break;
      }
      case CaptureType.CreateCellPhoneBundleWithExistingSubscription: {
        payload = this.createCellPhoneBundleWithExistingSubscription(product as MobilePhoneProduct);
        break;
      }
      case CaptureType.CreateAccessoryBundleWithCustomerSelectedANumber: {
        payload = this.createAccessoryBundleWithCustomerSelectedANumber(product as HardwareProduct);
        break;
      }
      case CaptureType.CreateAccessoryBundleWithPorting: {
        payload = this.createAccessoryBundleWithPorting(product as HardwareProduct);
        break;
      }
      case CaptureType.CreateAccessoryBundleWithExistingSubscription: {
        payload = this.createAccessoryBundleWithExistingSubscription(product as HardwareProduct);
        break;
      }
      case CaptureType.ChangeVoiceSimCard:
      case CaptureType.ChangeSimCard: {
        var voiceProduct = product as VoiceProduct;
        payload = this.ChangeSimCard(voiceProduct);
        break;
      }
      case CaptureType.AddMobileInsurance: {
        payload = this.addMobileInsurance(product as MobilePhoneProduct);
        break;
      }
      default: {
        payload = {};
        break;
      }
    }
    payload.idVerification = verification;
    return JSON.stringify(payload);
  }

  createSubscriptionWithCustomerSelectedAnumber(voiceProduct: VoiceProduct): CreateSubscriptionWithCustomerSelectedAnumber {
    const payload = {
      subscription: this.getSubscriptionWithCustomerSelectedANumber(voiceProduct),
      paymentType: PaymentType.Billing,
      idVerification: null,
    } as CreateSubscriptionWithCustomerSelectedAnumber;
    return payload;
  }

  private getSubscriptionWithCustomerSelectedANumber(voiceProduct: VoiceProduct): SubscriptionWithCustomerSelectedANumber {
    const { aNumber, availableNumbers, icc, orderSim, isESim, activationDateTime, isEarliestPortingDate } =
      voiceProduct.phoneNumber;
    const customerEmail = isESim
      ? this.store.selectSnapshot<CustomerStateModel>(state => state.customer)?.privateCustomer.email
      : null;

    return {
      subscriber: this.getSubscriber(voiceProduct),
      additionalServices: this.getAdditionalServices(voiceProduct),
      aNumber: aNumber,
      reservationId: availableNumbers.id,
      icc: icc || null,
      orderSim: orderSim || false,
      isESim: isESim || false,
      eSimActivationEmailAddress: customerEmail || null,
      idVerification: null,
      startDate: isEarliestPortingDate ? null : activationDateTime,
      sponsor: voiceProduct.sponsor,
      whitelistEntries: voiceProduct.whitelistEntries,
    } as SubscriptionWithCustomerSelectedANumber;
  }

  createSubscriptionWithGeneratedANumber(voiceProduct: VoiceProduct) {
    const payload = {
      subscription: this.getSubscriptionWithGeneratedANumber(voiceProduct),
      paymentType: PaymentType.Billing,
      idVerification: null,
    } as CreateSubscriptionWithGeneratedANumber;

    return payload;
  }

  private getSubscriptionWithGeneratedANumber(voiceProduct: VoiceProduct): SubscriptionWithGeneratedANumber {
    const { icc, orderSim, isESim, activationDateTime, isEarliestPortingDate } = voiceProduct.phoneNumber;
    const customerEmail = isESim
      ? this.store.selectSnapshot<CustomerStateModel>(state => state.customer)?.privateCustomer.email
      : null;

    return {
      subscriber: this.getSubscriber(voiceProduct),
      additionalServices: this.getAdditionalServices(voiceProduct),
      icc: icc || null,
      orderSim: orderSim || false,
      isESim: isESim || false,
      eSimActivationEmailAddress: customerEmail || null,
      idVerification: null,
      startDate: isEarliestPortingDate ? null : activationDateTime,
      sponsor: voiceProduct.sponsor,
      whitelistEntries: voiceProduct.whitelistEntries,
    } as SubscriptionWithGeneratedANumber;
  }

  createSubscriptionWithPorting(voiceProduct: VoiceProduct) {
    const payload = {
      subscription: this.getSubscriptionWithPorting(voiceProduct),
      paymentType: PaymentType.Billing,
      idVerification: null,
    } as CreateSubscriptionWithPorting;
    return payload;
  }

  private getSubscriptionWithPorting(voiceProduct: VoiceProduct): SubscriptionWithPorting {
    const {
      aNumber,
      keepExistingSim,
      existingOwnerCustomerId,
      activationDateTime,
      isEarliestPortingDate,
      icc,
      orderSim,
      isESim,
    } = voiceProduct.phoneNumber;
    const customerEmail = isESim
      ? this.store.selectSnapshot<CustomerStateModel>(state => state.customer)?.privateCustomer.email
      : null;

    return {
      subscriber: this.getSubscriber(voiceProduct),
      additionalServices: this.getAdditionalServices(voiceProduct),
      aNumber: aNumber,
      keepExistingSim: keepExistingSim,
      existingOwnerCustomerId: existingOwnerCustomerId,
      portingDateTime: isEarliestPortingDate ? null : activationDateTime,
      icc: icc || null,
      orderSim: orderSim || false,
      isESim: isESim || false,
      eSimActivationEmailAddress: customerEmail || null,
      idVerification: null,
      sponsor: voiceProduct.sponsor,
      whitelistEntries: voiceProduct.whitelistEntries,
    } as SubscriptionWithPorting;
  }

  createMBBSubscription(mbbProduct: MbbProduct): CreateMBBSubscription {
    const activationDate = mbbProduct.activationDate ? dateFns.startOfDay(new Date(mbbProduct.activationDate)) : null;

    const payload = {
      subscriber: this.getMbbSubscriber(),
      subscriptionInfo: this.getMbbSubscriptionInfo(mbbProduct),
      description: mbbProduct.mbbSubscription.name,
      icc: mbbProduct.icc,
      additionalServices: [],
      paymentType: mbbProduct.paymentType,
      idVerification: null,
      startDate: activationDate,
    } as CreateMBBSubscription;
    return payload;
  }

  createMBBSubscriptionWithHardware(mbbProduct: MbbProduct): CreateMBBSubscriptionWithHardware {
    const activationDate = mbbProduct.activationDate ? dateFns.startOfDay(new Date(mbbProduct.activationDate)) : null;
    const payload = {
      subscriber: this.getMbbSubscriber(),
      equipment: this.getMbbEquipment(mbbProduct),
      subscriptionInfo: this.getMbbSubscriptionInfo(mbbProduct),
      description: mbbProduct.mbbSubscription.name,
      routerName: mbbProduct.routerName,
      isSoldByExternalRetailOnly: mbbProduct.mbbRouter.externalRetailOnly ?? false,
      icc: mbbProduct.icc,
      additionalServices: [],
      paymentType: mbbProduct.paymentType,
      idVerification: null,
      startDate: activationDate,
    } as CreateMBBSubscriptionWithHardware;
    return payload;
  }

  createMBBSubscriptionWithHardwareAndLogistics(mbbProduct: MbbProduct): CreateMBBSubscriptionWithHardwareAndLogistics {
    const activationDate = mbbProduct.activationDate ? dateFns.startOfDay(new Date(mbbProduct.activationDate)) : null;

    const payload = {
      subscriber: this.getMbbSubscriber(),
      subsidyId: mbbProduct.selectedBundle.subsidyId,
      equipment: this.getMbbEquipment(mbbProduct),
      subscriptionInfo: this.getMbbSubscriptionInfo(mbbProduct),
      description: mbbProduct.mbbSubscription.name,
      routerName: mbbProduct.routerName,
      additionalServices: [],
      paymentType: mbbProduct.paymentType,
      hasShipment: mbbProduct.deliveryOption === MbbDeliveryOptions.SendRouterInPost,
      idVerification: null,
      startDate: activationDate,
    } as CreateMBBSubscriptionWithHardwareAndLogistics;
    return payload;
  }

  changeSubscriptionOnExistingCustomer(voiceProduct: VoiceProduct): ChangeSubscriptionOnExistingCustomer {
    const payload = {
      previousProductName: voiceProduct.subscription.productName,
      subscriber: this.getSubscriber(voiceProduct),
      aNumber: voiceProduct.subscription.msisdn,
      subscriptionId: voiceProduct.subscription.id,
      paymentType: PaymentType.Billing,
      additionalServices: SubscriptionHelperService.getChangedServicesOnExistingVoiceProduct(voiceProduct),
      imei: voiceProduct.imei,
      idVerification: null,
      sponsor: voiceProduct.sponsor,
      bindingType: hasAnnualBinding(voiceProduct) ? BindingType.AnnualBinding : BindingType.NoBinding,
    } as ChangeSubscriptionOnExistingCustomer;
    return payload;
  }

  changeSubscriberOnExistingSubscription(voiceProduct: VoiceProduct): ChangeSubscriberOnExistingSubscription {
    const payload = {
      subscriptionId: voiceProduct.subscription.id,
      subscriber: this.getSubscriber(voiceProduct),
      idVerification: null,
    } as ChangeSubscriberOnExistingSubscription;
    return payload;
  }

  addEquipment(hardwareProduct: HardwareProduct): AddEquipment {
    const payload = {
      subscriptionId: null,
      hasShipment: true,
      equipment: {
        ...hardwareProduct.equipment,
        description: hardwareProduct.equipment.name,
        imageUrl: hardwareProduct.equipment.image,
      },
      paymentType: hardwareProduct.paymentType,
      idVerification: null,
      insuranceOptionOfferingId: null,
    } as AddEquipment;
    return payload;
  }

  addMobileEquipment(mobileProduct: MobilePhoneProduct): AddEquipment {
    const payload = {
      subscriptionId: null,
      hasShipment: mobileProduct.sendInPost,
      equipment: {
        offeringId: mobileProduct.mobilePhoneVariant.offeringId,
        name: mobileProduct.mobilePhoneVariant.name,
        description: mobileProduct.mobilePhoneVariant.name, //TODO SAPO-3127
        gtin: mobileProduct.gtin,
        imei: mobileProduct.imei,
        price: mobileProduct.mobilePhoneVariant.priceInformation.price,
        imageUrl: mobileProduct.mobilePhoneVariant.image ? mobileProduct.mobilePhoneVariant.image.downloadUri : null,
      } as Equipment,
      paymentType: MobilePhoneProductHelperService.getMobilePaymentType(mobileProduct),
      idVerification: null,
      insuranceOptionOfferingId: mobileProduct.insuranceOption?.offeringId,
    } as AddEquipment;
    return payload;
  }

  updateServicesOnExistingSubscription(voiceProduct: VoiceProduct): UpdateServicesOnExistingSubscription {
    const payload = {
      subscriptionId: voiceProduct.subscription.id,
      additionalServices: SubscriptionHelperService.getChangedServicesOnExistingVoiceProduct(voiceProduct),
      paymentType: PaymentType.Billing,
      idVerification: null,
    } as UpdateServicesOnExistingSubscription;
    return payload;
  }

  changeMbbSubscriptionOnExistingCustomer(mbbProduct: MbbProduct): ChangeMbbSubscriptionOnExistingCustomer {
    const payload = {
      subscriptionId: mbbProduct.existingSubscription.id,
      subscriptionInfo: this.getMbbSubscriptionInfo(mbbProduct),
      paymentType: PaymentType.Billing,
      idVerification: null,
    } as ChangeMbbSubscriptionOnExistingCustomer;
    return payload;
  }

  private getSubsidyIdForVoiceProduct(mobilePhoneVariant: HardwareVariant, voiceProductOfferingId: string): string {
    return mobilePhoneVariant?.paymentInformation?.subscriptionBundleOptions?.find(
      s => s.subscriptionOfferingId === voiceProductOfferingId
    )?.subsidyId;
  }

  createCellPhoneBundleWithCustomerSelectedANumber(
    mobileProduct: MobilePhoneProduct
  ): CreateCellPhoneBundleWithCustomerSelectedANumber {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const connectedVoiceProduct = allVoiceProducts.find(p => p.id === mobileProduct.voiceProductId);

    const paymentType = MobilePhoneProductHelperService.getMobilePaymentType(mobileProduct);
    const cellPhoneAgreement = this.getCellPhoneAgreementCaptureObject(paymentType, mobileProduct, connectedVoiceProduct);
    const subsidyId = this.getSubsidyIdForVoiceProduct(mobileProduct.mobilePhoneVariant, connectedVoiceProduct.offering.id);

    const payload = {
      paymentType,
      hasShipment: mobileProduct.sendInPost,
      isExternalCellPhone: mobileProduct.isExternalCellPhone,
      cellPhoneAgreement,
      subscriptionOfferingId: connectedVoiceProduct.offering.id,
      subsidyId,
      subscription: this.getSubscriptionWithCustomerSelectedANumber(connectedVoiceProduct),
      idVerification: null,
      insuranceOptionOfferingId: mobileProduct.insuranceOption?.offeringId,
      insuranceOptionOfferingName: mobileProduct.insuranceOption?.name,
      eid: connectedVoiceProduct.phoneNumber.eid,
    } as CreateCellPhoneBundleWithCustomerSelectedANumber;

    return payload;
  }

  createCellPhoneBundleWithGeneratedANumber(mobileProduct: MobilePhoneProduct): CreateCellPhoneBundleWithGeneratedANumber {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const connectedVoiceProduct = allVoiceProducts.find(p => p.id === mobileProduct.voiceProductId);

    const paymentType = MobilePhoneProductHelperService.getMobilePaymentType(mobileProduct);
    const cellPhoneAgreement = this.getCellPhoneAgreementCaptureObject(paymentType, mobileProduct, connectedVoiceProduct);
    const subsidyId = this.getSubsidyIdForVoiceProduct(mobileProduct.mobilePhoneVariant, connectedVoiceProduct.offering.id);

    const payload = {
      paymentType,
      hasShipment: mobileProduct.sendInPost,
      isExternalCellPhone: mobileProduct.isExternalCellPhone,
      cellPhoneAgreement,
      subscriptionOfferingId: connectedVoiceProduct.offering.id,
      subsidyId,
      subscription: this.getSubscriptionWithGeneratedANumber(connectedVoiceProduct),
      idVerification: null,
      insuranceOptionOfferingId: mobileProduct.insuranceOption?.offeringId,
      insuranceOptionOfferingName: mobileProduct.insuranceOption?.name,
    } as CreateCellPhoneBundleWithGeneratedANumber;

    return payload;
  }

  createCellPhoneBundleWithPorting(mobileProduct: MobilePhoneProduct): CreateCellPhoneBundleWithPorting {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const connectedVoiceProduct = allVoiceProducts.find(p => p.id === mobileProduct.voiceProductId);

    const paymentType = MobilePhoneProductHelperService.getMobilePaymentType(mobileProduct);
    const cellPhoneAgreement = this.getCellPhoneAgreementCaptureObject(paymentType, mobileProduct, connectedVoiceProduct);
    const subsidyId = this.getSubsidyIdForVoiceProduct(mobileProduct.mobilePhoneVariant, connectedVoiceProduct.offering.id);

    const payload = {
      paymentType,
      hasShipment: mobileProduct.sendInPost,
      isExternalCellPhone: mobileProduct.isExternalCellPhone,
      cellPhoneAgreement,
      subscriptionOfferingId: connectedVoiceProduct.offering.id,
      subsidyId,
      subscription: this.getSubscriptionWithPorting(connectedVoiceProduct),
      idVerification: null,
      insuranceOptionOfferingId: mobileProduct.insuranceOption?.offeringId,
      insuranceOptionOfferingName: mobileProduct.insuranceOption?.name,
    } as CreateCellPhoneBundleWithPorting;

    return payload;
  }

  createCellPhoneBundleWithExistingSubscription(
    mobileProduct: MobilePhoneProduct
  ): CreateCellPhoneBundleWithExistingSubscription {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const connectedVoiceProduct = allVoiceProducts.find(p => p.id === mobileProduct.voiceProductId);

    const paymentType = MobilePhoneProductHelperService.getMobilePaymentType(mobileProduct);
    const cellPhoneAgreement = this.getCellPhoneAgreementCaptureObjectForExistingSubscription(paymentType, mobileProduct);
    const subsidyId = this.getSubsidyIdForVoiceProduct(
      mobileProduct.mobilePhoneVariant,
      connectedVoiceProduct?.offering?.id
    );
    const payload = {
      paymentType,
      hasShipment: mobileProduct.sendInPost,
      isExternalCellPhone: mobileProduct.isExternalCellPhone,
      cellPhoneAgreement,
      subsidyId,
      subscriptionOfferingId: connectedVoiceProduct?.offering?.id,
      existingSubscriptionId: +connectedVoiceProduct.subscription.id,
      idVerification: null,
      insuranceOptionOfferingId: mobileProduct.insuranceOption?.offeringId,
      insuranceOptionOfferingName: mobileProduct.insuranceOption?.name,
      msisdn: connectedVoiceProduct.subscription.msisdn,
    } as CreateCellPhoneBundleWithExistingSubscription;

    return payload;
  }

  private captureTradeInProduct(): string {
    const hasAcceptedTradeInVoucher = this.store.selectSnapshot(TradeInSelectors.hasAcceptedTradeInVoucher);
    if (!hasAcceptedTradeInVoucher) return null;

    const tradeInProduct = this.store.selectSnapshot(TradeInSelectors.tradeInProduct);
    const { voucherId, voucherAmount } = this.store.selectSnapshot(TradeInSelectors.quote);
    const { size, unit } = this.store.selectSnapshot(TradeInSelectors.selectedStorageCapacity);
    const verification = this.store.selectSnapshot(FormSelectors.getCustomerVerification);
    const isRetailPayment = this.store.selectSnapshot(DealerStateSelectors.isRetailPayment);

    const payload: CaptureTradeInProduct = {
      voucherId: voucherId,
      amount: this.mapAmountPriceToPrice(voucherAmount),
      tradeInId: tradeInProduct?.tradeInVoucher?.tradeInId,
      imei: tradeInProduct?.specifications?.imei,
      deviceVendor: tradeInProduct?.specifications?.deviceVendor,
      deviceModel: tradeInProduct?.specifications?.deviceModel,
      storageCapacity: `${size} ${unit}`,
      idVerification: verification,
      paymentType: isRetailPayment ? UpfrontPaymentMethod.NoUpfrontPayment : PaymentType.Upfront,
      hasShipment: !isRetailPayment,
    };

    return JSON.stringify(payload);
  }

  private captureVendorSubsidyVoucher(): string {
    const vendorSubsidy = this.store.selectSnapshot(TradeInSelectors.vendorSubsidy);
    const verification = this.store.selectSnapshot(FormSelectors.getCustomerVerification);
    const isRetailPayment = this.store.selectSnapshot(DealerStateSelectors.isRetailPayment);
    const gtins = this.store.selectSnapshot(CustomerProductMobilePhoneSelectors.getMobilePhoneGtins);

    if (!vendorSubsidy) return null;

    const payload: CaptureVendorSubsidyVoucher = {
      voucherId: vendorSubsidy.voucherId,
      amount: this.mapAmountPriceToPrice(vendorSubsidy.voucherAmount),
      vendorName: vendorSubsidy.providedByVendor,
      periodName: vendorSubsidy.periodName,
      validUntil: vendorSubsidy.periodValidUntil,
      purchasedDevices: gtins,
      idVerification: verification,
      paymentType: isRetailPayment ? UpfrontPaymentMethod.NoUpfrontPayment : PaymentType.Upfront,
      hasShipment: !isRetailPayment,
    };

    return JSON.stringify(payload);
  }

  createAccessoryBundleWithCustomerSelectedANumber(
    hardwareProduct: HardwareProduct
  ): CreateAccessoryBundleWithCustomerSelectedANumber {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const connectedVoiceProduct = allVoiceProducts.find(p => p.id === hardwareProduct.voiceProductId);
    const isManualRegistration = hardwareProduct.equipment.isManualRegistration;
    const price = isManualRegistration
      ? null
      : this.mapAmountPriceToPrice(hardwareProduct.selectedSubscription.hardwareOfferingPriceWithSubscription);

    const payload = {
      subscription: this.getSubscriptionWithCustomerSelectedANumber(connectedVoiceProduct),
      subsidyId: hardwareProduct.selectedSubscription.subsidyId,
      subscriptionOfferingId: connectedVoiceProduct.offering.id,
      hasShipment: !isManualRegistration,
      paymentType: AccessoryProductHelperService.getAccessoryPaymentType(hardwareProduct),
      name: hardwareProduct.equipment.name,
      imageUrl: hardwareProduct.equipment.image,
      totalPrice: price,
      gtin: hardwareProduct.equipment.gtin,
      imei: hardwareProduct.equipment.imei,
      isManualRegistration: isManualRegistration,
    } as CreateAccessoryBundleWithCustomerSelectedANumber;

    return payload;
  }

  createAccessoryBundleWithPorting(hardwareProduct: HardwareProduct): CreateAccessoryBundleWithPorting {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const connectedVoiceProduct = allVoiceProducts.find(p => p.id === hardwareProduct.voiceProductId);
    const isManualRegistration = hardwareProduct.equipment.isManualRegistration;
    const price = isManualRegistration
      ? null
      : this.mapAmountPriceToPrice(hardwareProduct.selectedSubscription.hardwareOfferingPriceWithSubscription);

    const payload = {
      subscription: this.getSubscriptionWithPorting(connectedVoiceProduct),
      subsidyId: hardwareProduct.selectedSubscription.subsidyId,
      subscriptionOfferingId: connectedVoiceProduct.offering.id,
      hasShipment: !isManualRegistration,
      paymentType: AccessoryProductHelperService.getAccessoryPaymentType(hardwareProduct),
      name: hardwareProduct.equipment.name,
      imageUrl: hardwareProduct.equipment.image,
      totalPrice: price,
      gtin: hardwareProduct.equipment.gtin,
      imei: hardwareProduct.equipment.imei,
      isManualRegistration: isManualRegistration,
    } as CreateAccessoryBundleWithPorting;

    return payload;
  }

  createAccessoryBundleWithExistingSubscription(
    hardwareProduct: HardwareProduct
  ): CreateAccessoryBundleWithExistingSubscription {
    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts);
    const connectedVoiceProduct = allVoiceProducts.find(p => p.id === hardwareProduct.voiceProductId);
    const isManualRegistration = hardwareProduct.equipment.isManualRegistration;
    const price = isManualRegistration
      ? null
      : this.mapAmountPriceToPrice(hardwareProduct.selectedSubscription.hardwareOfferingPriceWithSubscription);

    const payload = {
      existingSubscriptionId: connectedVoiceProduct.subscription.id,
      subsidyId: hardwareProduct.selectedSubscription.subsidyId,
      subscriptionOfferingId: connectedVoiceProduct.offering.id,
      hasShipment: !isManualRegistration,
      paymentType: AccessoryProductHelperService.getAccessoryPaymentType(hardwareProduct),
      name: hardwareProduct.equipment.name,
      imageUrl: hardwareProduct.equipment.image,
      totalPrice: price,
      gtin: hardwareProduct.equipment.gtin,
      imei: hardwareProduct.equipment.imei,
      isManualRegistration: isManualRegistration,
    } as CreateAccessoryBundleWithExistingSubscription;

    return payload;
  }

  ChangeSimCard(voiceProduct: VoiceProduct): CaptureChangeSimCard {
    let icc = '';
    let isESim = false;
    let aNumber = '';
    if (voiceProduct.replaceVoiceSimIcc) {
      icc = voiceProduct.replaceVoiceSimIcc;
      aNumber = voiceProduct.subscription.msisdn;
    } else if (voiceProduct.replaceVoiceESim) {
      isESim = true;
      aNumber = voiceProduct.subscription.msisdn;
    } else if (voiceProduct.replaceDataSimIcc) {
      icc = voiceProduct.replaceDataSimIcc;
      aNumber =
        voiceProduct.replaceAdditionalSimANumber ?? voiceProduct.additionalServices.find(s => s.isDataSimCard).aNumber;
    } else if (voiceProduct.replaceTwinSimIcc) {
      icc = voiceProduct.replaceTwinSimIcc;
      aNumber =
        voiceProduct.replaceAdditionalSimANumber ?? voiceProduct.additionalServices.find(s => s.isTwinSimCard).aNumber;
    }

    const payload = {
      aNumber: aNumber,
      icc: icc,
      isESim: isESim,
      subscriptionId: voiceProduct.subscription.id,
      startDate: new Date(),
      idVerification: null,
    } as CaptureChangeSimCard;

    return payload;
  }

  captureSendReturnBox(): string {
    const verification = this.store.selectSnapshot(FormSelectors.getCustomerVerification);
    const payload = {
      idVerification: verification,
      hasShipment: true,
      paymentType: PaymentType.Billing, // PaymentType is not valid for returnbox since it is free, so setting it to default billing. This will be ignored in sps capture
    } as CaptureSendReturnBox;

    return JSON.stringify(payload);
  }

  private captureCustomerServiceProduct(product: CustomerServiceProduct): string {
    const verification = this.store.selectSnapshot(FormSelectors.getCustomerVerification);
    const payload = {
      idVerification: verification,
      hasShipment: false,
      paymentType: PaymentType.Billing,
      monthlyPrice: product.customerServiceOffering.monthlyPrice,
      name: product.customerServiceOffering.name,
    } as CaptureCreateCustomerService;

    return JSON.stringify(payload);
  }

  addMobileInsurance(mobileProduct: MobilePhoneProduct): AddMobileInsurance {
    const payload = {
      imei: mobileProduct.imei,
      hasShipment: mobileProduct.sendInPost,
      idVerification: null,
    } as AddMobileInsurance;
    return payload;
  }

  private getCellPhoneAgreementCaptureObject(
    paymentType: PaymentType,
    mobileProduct: MobilePhoneProduct,
    connectedVoiceProduct: VoiceProduct
  ): CellPhoneAgreement {
    let totalPrice: Price;
    let monthlyPrice: Price;
    let terminalPrice: Price;
    let monthlyInsuranceCost: Price;
    let agreementPeriodInMonths: number;
    let upgradeAfterNumberOfMonths: number;
    let redemptionId: number;

    if (paymentType === PaymentType.EquipmentUpgrade) {
      const selectedMobilBytteOfferingId = mobileProduct.mobilBytteOffering;
      const selectedInsuranceOption = mobileProduct.insuranceOption;
      const selectedMobilBytte = mobileProduct.mobilePhoneVariant.mobilbytteOfferings?.mobilbytteOfferings.find(
        offering => offering.offeringId === selectedMobilBytteOfferingId
      );

      totalPrice = this.mapAmountPriceToPrice(selectedMobilBytte.totalPriceAfterAgreementPeriod);
      monthlyPrice = this.mapAmountPriceToPrice(selectedMobilBytte.monthlyPrice);
      monthlyInsuranceCost = selectedInsuranceOption?.priceInformation.price;
      terminalPrice = this.mapAmountPriceToPrice(selectedMobilBytte.recommendedRetailPrice);
      agreementPeriodInMonths = selectedMobilBytte.agreementPeriodInMonths;
      upgradeAfterNumberOfMonths = selectedMobilBytte.minimumNumberOfMonthsBeforeUpgrade;

      if (mobileProduct.existingAgreementId) {
        const existingEquipmentUpgradeAgreement = this.store
          .selectSnapshot<CustomerEquipmentUpgradeAgreement[]>(MobilBytteSelectors.existingEquipmentUpgradeAgreements)
          ?.find(a => a.agreementId === mobileProduct.existingAgreementId);
        redemptionId = existingEquipmentUpgradeAgreement?.redemptionId;
      }
    } else if (paymentType === PaymentType.Upfront) {
      if (mobileProduct.paymentType === MobilePaymentType.PayNowWithSubscription) {
        const bundleMobilePrice = mobileProduct.mobilePhoneVariant.paymentInformation.subscriptionBundleOptions.find(
          o => o.subscriptionOfferingId === connectedVoiceProduct.offering?.id
        );
        totalPrice = bundleMobilePrice.priceInformation.price;
        agreementPeriodInMonths = 0;
      } else {
        totalPrice = mobileProduct.mobilePhoneVariant.priceInformation.price;
        agreementPeriodInMonths = 0;
      }
    }
    const cellPhoneAgreement: CellPhoneAgreement = {
      imei: mobileProduct.imei,
      gtin: mobileProduct?.gtin,

      name: mobileProduct.mobilePhoneVariant?.name || mobileProduct.name || '',
      imageUrl: mobileProduct.mobilePhoneVariant?.image ? mobileProduct.mobilePhoneVariant.image.downloadUri : null,
      isIdentifiersPendingUpdate: !!mobileProduct.isMissingIdentifiers,
      cellPhoneAgreementPrice: mobileProduct.isExternalCellPhone
        ? null
        : ({
            monthlyPrice: monthlyPrice,
            totalPrice: totalPrice,
            monthlyInsuranceCost: monthlyInsuranceCost,
            terminalPrice: terminalPrice,
            agreementPeriodInMonths: agreementPeriodInMonths,
            upgradeAfterNumberOfMonths: upgradeAfterNumberOfMonths,
          } as CellPhoneAgreementPrice),
      redemptionId: redemptionId,
    } as CellPhoneAgreement;
    return cellPhoneAgreement;
  }

  private getCellPhoneAgreementCaptureObjectForExistingSubscription(
    paymentType: PaymentType,
    mobileProduct: MobilePhoneProduct
  ): CellPhoneAgreement {
    let totalPrice: Price;
    let monthlyPrice: Price;
    let terminalPrice: Price;
    let monthlyInsuranceCost: Price;
    let agreementPeriodInMonths: number;
    let upgradeAfterNumberOfMonths: number;
    let redemptionId: number;

    const allVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(s => s.customerProducts.products);

    if (paymentType === PaymentType.EquipmentUpgrade) {
      const selectedMobilBytteOfferingId = mobileProduct.mobilBytteOffering;
      const selectedInsuranceOption = mobileProduct.insuranceOption;
      const selectedMobilBytte = mobileProduct.mobilePhoneVariant.mobilbytteOfferings?.mobilbytteOfferings.find(
        offering => offering.offeringId === selectedMobilBytteOfferingId
      );

      totalPrice = this.mapAmountPriceToPrice(selectedMobilBytte.totalPriceAfterAgreementPeriod);
      monthlyPrice = this.mapAmountPriceToPrice(selectedMobilBytte.monthlyPrice);
      monthlyInsuranceCost = selectedInsuranceOption?.priceInformation.price;
      terminalPrice = this.mapAmountPriceToPrice(selectedMobilBytte.recommendedRetailPrice);
      agreementPeriodInMonths = selectedMobilBytte.agreementPeriodInMonths;
      upgradeAfterNumberOfMonths = selectedMobilBytte.minimumNumberOfMonthsBeforeUpgrade;

      if (mobileProduct.existingAgreementId) {
        const existingEquipmentUpgradeAgreement = this.store
          .selectSnapshot<CustomerEquipmentUpgradeAgreement[]>(MobilBytteSelectors.existingEquipmentUpgradeAgreements)
          ?.find(a => a.agreementId === mobileProduct.existingAgreementId);
        redemptionId = existingEquipmentUpgradeAgreement?.redemptionId;
      }
    } else if (paymentType === PaymentType.Upfront) {
      const subscriptionBundlePrice = MobilePhoneProductHelperService.getSubscriptionBundlePrice(
        mobileProduct,
        allVoiceProducts
      );
      totalPrice = subscriptionBundlePrice.totalPrice;
      agreementPeriodInMonths = subscriptionBundlePrice.agreementLength;
    }
    const cellPhoneAgreement: CellPhoneAgreement = {
      name: mobileProduct.mobilePhoneVariant?.name || mobileProduct.name || '',
      imageUrl: mobileProduct.mobilePhoneVariant?.image ? mobileProduct.mobilePhoneVariant.image.downloadUri : null,
      imei: mobileProduct.imei,
      gtin: mobileProduct?.gtin,
      isIdentifiersPendingUpdate: !!mobileProduct.isMissingIdentifiers,
      cellPhoneAgreementPrice: mobileProduct.isExternalCellPhone
        ? null
        : ({
            monthlyPrice: monthlyPrice,
            totalPrice: totalPrice,
            monthlyInsuranceCost: monthlyInsuranceCost,
            terminalPrice: terminalPrice,
            agreementPeriodInMonths: agreementPeriodInMonths,
            upgradeAfterNumberOfMonths: upgradeAfterNumberOfMonths,
          } as CellPhoneAgreementPrice),
      redemptionId: redemptionId,
    } as CellPhoneAgreement;
    return cellPhoneAgreement;
  }

  getSubscriber({ subscriber }: VoiceProduct): CaptureSubscriber {
    return {
      name: subscriber.name,
      email: subscriber.dontHaveEmail ? null : subscriber.email,
      birthdate: subscriber.birthday,
      streetAddress: subscriber.address,
      postalCode: subscriber.postCode,
      addressCity: subscriber.city,
      phoneBookVisibility: subscriber.phoneBookVisibility,
    } as CaptureSubscriber;
  }

  getMbbSubscriber(): CaptureSubscriber {
    const customer = this.store.selectSnapshot<CustomerStateModel>(state => state.customer);
    return {
      name: customer.privateCustomer.fullname,
      email: customer.privateCustomer.email,
      birthdate: customer.privateCustomer.birthdate,
      streetAddress: customer.privateCustomer.address,
      postalCode: customer.privateCustomer.postCode,
      addressCity: customer.privateCustomer.city,
    } as CaptureSubscriber;
  }

  getMbbEquipment(mbbProduct: MbbProduct): Equipment {
    return {
      offeringId: mbbProduct.mbbRouter.offeringId,
      name: mbbProduct.mbbRouter.name,
      description: mbbProduct.mbbRouter.name,
      gtin: mbbProduct.mbbRouter.gtin,
      imei: mbbProduct.imei,
      price: mbbProduct.selectedBundle.hardwarePrice,
      imageUrl: mbbProduct.mbbRouter.image?.downloadUri,
    } as Equipment;
  }

  getMbbSubscriptionInfo(mbbProduct: MbbProduct): SubscriptionInfo {
    return {
      subscriptionOfferingId: mbbProduct.mbbSubscription.id,
      subscriptionName: mbbProduct.mbbSubscription.name,
      campaignPeriodMonths: mbbProduct.mbbSubscription.monthlyPrice.campaignPeriod,
      monthlyPrice: mbbProduct.mbbSubscription.monthlyPrice.price,
      originalMonthlyPrice: mbbProduct.mbbSubscription.monthlyPrice.originalPrice,
      bindingPeriodMonths: mbbProduct.mbbSubscription.bindings[0]?.period ?? 0,
      terminationFee: mbbProduct.mbbSubscription.bindings[0]?.terminationFee,
    } as SubscriptionInfo;
  }

  getAdditionalServices(voiceProduct: VoiceProduct): AdditionalService[] {
    return voiceProduct.additionalServices.map(service => {
      return {
        productId: service.productId,
        description: service.description,
        icc: service.icc,
        isCampaign: service.isCampaign,
        monthlyPrice: service.price,
        isServiceAdded: true,
      } as AdditionalService;
    });
  }

  getOneTimeInvoicePayment(mbbProduct: MbbProduct): Price {
    if (mbbProduct.paymentType !== PaymentType.PayWithNextInvoice) {
      return null;
    }

    return new Price(
      mbbProduct.selectedBundle.hardwarePrice.includingVAT,
      mbbProduct.selectedBundle.hardwarePrice.excludingVAT,
      mbbProduct.selectedBundle.hardwarePrice.vatPercentage
    );
  }

  getProductOrderRequest(externalShoppingCartId: string): ProductOrderRequest {
    const customer = this.store.selectSnapshot<CustomerStateModel>(state => state.customer);
    const logistics = this.store.selectSnapshot<LogisticsStateModel>(state => state.logistics);
    const order = this.store.selectSnapshot<OrderStateModel>(state => state.order);
    const isServiceOrder = this.store.selectSnapshot<boolean>(state => state.order.isServiceOrder);
    const isBankIdBypassed = this.store.selectSnapshot<boolean>(state => state.order.isBankIdBypassed);
    const userId = this.store.selectSnapshot<string>(state => state.user.user.sub);
    const customerHasSecretNumber = this.store.selectSnapshot<boolean>(CustomerProductSelectors.customerHasSecretNumber);
    const verification = this.store.selectSnapshot(FormSelectors.getCustomerVerification);

    const productOrderRequest = new ProductOrderRequest();

    productOrderRequest.category = isServiceOrder ? OrderCategories.ServiceOrder : OrderCategories.B2CProductOrder;
    productOrderRequest.relatedParties = this.getRelatedParties();
    productOrderRequest.notes = [];
    productOrderRequest.upfrontPaymentMethod = order.upfrontPaymentMethod;
    productOrderRequest.upfrontPaymentContactNumber = order.upfrontPaymentContactNumber || customer.privateCustomer.phone;
    productOrderRequest.externalShoppingCartId = externalShoppingCartId;
    productOrderRequest.customerDetails = new CustomerDetails();

    if (verification.verificationType === 'bankid') {
      productOrderRequest.bankIdVerificationId = customer.bankIdVerification?.id;
    }
    if (!isBankIdBypassed) {
      productOrderRequest.bankIdMsisdn = order.bankIdMsisdn;
      productOrderRequest.bankIdEmail = order.bankIdEmail;
    } else {
      productOrderRequest.bypassInfoRequest = {
        bypassCategory: BypassCategory.BankId,
        notes: order.bankIdBypassNote,
        bypassedByUserId: userId,
      } as BypassInfoRequest;
    }
    // Set customer details
    productOrderRequest.customerDetails.hasSecretNumber = customerHasSecretNumber;
    productOrderRequest.customerDetails.phone = customer.privateCustomer.phone;
    productOrderRequest.customerDetails.customerAllowance = {
      firstName: customer.privateCustomer.firstname,
      lastName: customer.privateCustomer.lastname,
      SSN: customer.privateCustomer.ssn,
    } as CustomerAllowance;

    if (!customer.isExistingCustomer) {
      productOrderRequest.customerDetails.email = customer.privateCustomer.email;
      productOrderRequest.customerDetails.newCustomerAddress = {
        city: customer.privateCustomer.city,
        postalCode: customer.privateCustomer.postCode,
        streetAddress: customer.privateCustomer.address,
      } as CustomerAddress;

      productOrderRequest.customerDetails.newCustomerConsentChecked = customer.consentChecked;
      productOrderRequest.customerDetails.newCustomerInvoiceType = customer.invoiceType;
    } else {
      productOrderRequest.customerDetails.existingCustomerId = customer.customerId;
      productOrderRequest.customerDetails.email = customer.privateCustomer.email;
    }

    // Set delivery details
    productOrderRequest.customerDetails.deliveryRecipientName = `${customer.privateCustomer.deliveryAddress.firstName} ${customer.privateCustomer.deliveryAddress.lastName}`;
    productOrderRequest.customerDetails.deliveryAddress = {
      streetAddress: customer.privateCustomer.deliveryAddress.address,
      postalCode: customer.privateCustomer.deliveryAddress.postCode,
      city: customer.privateCustomer.deliveryAddress.city,
    } as CustomerDeliveryAddress;

    if (logistics.selectedDeliveryOptionId) {
      const name = this.getDeliveryOptionName(logistics.selectedOfferingId, logistics.deliveryOptions);
      productOrderRequest.shipmentOption = new ShipmentOption();
      productOrderRequest.shipmentOption.name = name;
      productOrderRequest.shipmentOption.offeringId = logistics.selectedOfferingId;
      productOrderRequest.shipmentOption.deliveryOptionId = logistics.selectedDeliveryOptionId.toString();
    }

    return productOrderRequest;
  }

  getDeliveryOptionName(offeringId: string, deliveryOptions: DeliveryOptionDetails[]): string {
    const matchingDeliveryOption = deliveryOptions.find(x => x.offeringId === offeringId);
    return matchingDeliveryOption ? matchingDeliveryOption.displayName : '';
  }

  deserializeCaptureData(captureDataString: string): any {
    if (!captureDataString) {
      return null;
    }

    try {
      let result = JSON.parse(captureDataString);
      // Some legacy capture data was stored double-encoded (the JSON was stringified twice),
      // in which case the first parse yields a string that needs to be parsed once more.
      if (typeof result === 'string') {
        result = JSON.parse(result);
      }

      return result;
    } catch {
      return null;
    }
  }

  private mapAmountPriceToPrice(amount: AmountPrice): Price {
    return new Price(amount.amountIncludingVAT, amount.amountExcludingVAT, amount.vatPercentage);
  }

  private getRelatedParties(): RelatedParty[] {
    const dealer = this.store.selectSnapshot<Dealer>(DealerStateSelectors.activeDealer);
    const actorId = this.store.selectSnapshot(UserSelectors.userId);
    const sellerId = this.store.selectSnapshot(OrderSelectors.salesPersonId);

    return [
      {
        id: dealer.dealerId,
        name: 'dealer',
      },
      {
        id: actorId,
        name: 'actor',
      },
      {
        id: sellerId,
        name: 'seller',
      },
    ];
  }
}
